// src/pages/ResetForgottenPassword.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/GMF logo no background.png";
import styles from "../Styles/Auth.module.css"; // ← default import, not destructured

function ResetForgottenPassword() {
	const [step, setStep] = useState(1);
	const [email, setEmail] = useState("");
	const [isEmailValid, setIsEmailValid] = useState(false);
	const [emailTouched, setEmailTouched] = useState(false);
	const [userType, setUserType] = useState("");
	const [roleError, setRoleError] = useState(false);
	const [securityQuestions, setSecurityQuestions] = useState([]);
	const [securityAnswers, setSecurityAnswers] = useState({});
	const [newPassword, setNewPassword] = useState("");
	const [confirmNewPassword, setConfirmNewPassword] = useState("");
	const [passwordRequirements, setPasswordRequirements] = useState({
		minLength: false,
		hasSpecialChar: false,
	});
	// if the first password input field has been touched
	const [passwordTouched, setPasswordTouched] = useState(false);
	// if the confirm password input field has been touched
	const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
	const [confirmPasswordError, setConfirmPasswordError] = useState("");

	const [error, setError] = useState("");
	const [message, setMessage] = useState("");

	const navigate = useNavigate();
	const url = process.env.REACT_APP_API_URL;
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	// Add password validation function
	const validatePassword = (password) => {
		const requirements = {
			minLength: password.length >= 12,
			hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
		};
		setPasswordRequirements(requirements);
		return Object.values(requirements).every(Boolean);
	};

	// for the first password
	// Handle password input changes
	const handleNewPasswordChange = (e) => {
		const value = e.target.value;
		setNewPassword(value);
		validatePassword(value);
	};

	// Mark password field as touched when it loses focus
	const handleNewPasswordBlur = () => {
		setPasswordTouched(true);
	};

	// for the confirm password
	// Handle confirm password input changes
	const handleConfirmPasswordChange = (e) => {
		const value = e.target.value;
		setConfirmNewPassword(value);

		// Validate confirm password in real-time
		if (newPassword !== value) {
			setConfirmPasswordError("Passwords do not match.");
		} else {
			setConfirmPasswordError("");
		}
	};

	// Mark confirm password field as touched when it loses focus
	const handleConfirmPasswordBlur = () => {
		setConfirmPasswordTouched(true);
	};

	const handleEmailChange = (e) => {
		const value = e.target.value;
		setEmail(value);
		setIsEmailValid(emailRegex.test(value)); // Validate email in real-time
	};

	const handleEmailBlur = () => {
		setEmailTouched(true); // Mark email as touched when the user leaves the field
	};

	const handleEmailSubmit = async (e) => {
		e.preventDefault(); // Prevent the default form submission behavior
		setError(""); // Clear any previous errors
		setEmailTouched(true);

		// Check if the user has selected a role
		if (!userType) {
			setRoleError(true); // Show an error if no role is selected
			return;
		}

		// Check if the email is valid
		if (!isEmailValid) return; // Do nothing (i.e. don't submit) if the email is invalid

		try {
			// Determine the endpoint based on the selected role
			const endpoint =
				userType === "GMF"
					? `${url}/gmf-employee/forgot-password/security-questions`
					: `${url}/institution-account/forgot-password/security-questions`;

			// Make a POST request to fetch the security questions for this user
			const response = await fetch(endpoint, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email }),
			});

			if (response.ok) {
				const data = await response.json(); // Parse the JSON response

				// Transform the API response into an array of questions
				const questions = [
					{ id: 1, question: data.security_question_1 },
					{ id: 2, question: data.security_question_2 },
					{ id: 3, question: data.security_question_3 },
				];

				setSecurityQuestions(questions); // Update the state with the transformed questions
				setStep(2); // Move to the next step (answering security questions)
			} else {
				const data = await response.json();
				setError(data.message || "Failed to fetch security questions.");
			}
		} catch {
			setError("An error occurred. Please try again."); // Handle any network or server errors
		}
	};

	const handleResetSubmit = async (e) => {
		e.preventDefault();
		setError("");

		if (newPassword !== confirmNewPassword) {
			setError("Passwords do not match.");
			return;
		}

		try {
			const endpoint =
				userType === "GMF"
					? `${url}/gmf-employee/forgot-password/verify`
					: `${url}/institution-account/forgot-password/verify`;

			// Correctly map securityAnswers to the expected backend fields
			const requestBody = {
				email,
				security_answer_1: securityAnswers[1] || "", // Map question 1 to security_answer_1
				security_answer_2: securityAnswers[2] || "", // Map question 2 to security_answer_2
				security_answer_3: securityAnswers[3] || "", // Map question 3 to security_answer_3
				new_password: newPassword,
			};

			const response = await fetch(endpoint, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(requestBody),
			});

			if (response.ok) {
				setMessage("Password reset successfully!");
				alert(
					"Success! Your password has been changed."
				);
				navigate("/");
			} else {
				const data = await response.json();
				setError(data.message || "Failed to reset password.");
			}
		} catch {
			setError("An error occurred. Please try again.");
		}
	};

	const handleAnswerChange = (id, value) => {
		setSecurityAnswers((prev) => ({ ...prev, [id]: value }));
	};

	return (
		<div className={styles.home}>
			<div className={styles.headerSection}>
				<div className={styles.contentContainer}>
					{/* <img
                        src={logo}
                        alt="MD-Sensei Logo"
                        className={styles.logo}
                    /> */}

					{step === 1 && (
						<>
							<h1 className={styles.title}>
								Reset Forgotten Password
							</h1>
							<p className={styles.instructions}>
								Forgot your password? No worries! Just select
								your role, enter your email, and we'll fetch
								your custom list of security questions. Once you
								answer them, you can reset your password and get
								back to using your account. 😊
							</p>

							<form onSubmit={handleEmailSubmit}>
								<div className={styles.formGroup}>
									<label>Select Your Role:</label>
									<div className={styles.roleSelection}>
										<button
											type="button"
											className={
												userType === "GMF"
													? styles.selectedButton
													: styles.unselectedButton
											}
											onClick={() => {
												setUserType("GMF");
												setRoleError(false);
											}}
										>
											Global Medic Force
										</button>
										<button
											type="button"
											className={
												userType === "institution"
													? styles.selectedButton
													: styles.unselectedButton
											}
											onClick={() => {
												setUserType("institution");
												setRoleError(false);
											}}
										>
											Institution (e.g. Doctors Without
											Borders, Ministry of Health)
										</button>
									</div>
									{roleError && (
										<p className={styles.errorMessage}>
											Please select your role.
										</p>
									)}
								</div>

								<div className={styles.formGroup}>
									<label>Email:</label>
									<input
										type="email"
										value={email}
										onChange={handleEmailChange}
										onBlur={handleEmailBlur}
										className={
											!isEmailValid && emailTouched
												? styles.errorInput
												: ""
										}
										required
									/>
									{emailTouched && !isEmailValid && (
										<p className={styles.errorMessage}>
											Please enter a valid email address.
										</p>
									)}
								</div>

								<button
									type="submit"
									className={styles.submitButton}
									disabled={!isEmailValid}
								>
									Fetch Security Questions
								</button>

								{error && (
									<p className={styles.errorMessage}>
										{error}
									</p>
								)}
							</form>
						</>
					)}

					{step === 2 && (
						<>
							<h1 className={styles.title}>
								Email Success! Now Answer Security Questions
							</h1>
							<p className={styles.instructions}>
								Your email submission was successful! Please
								answer the security questions below and choose a
								new password to reset your account.
							</p>
							<p className={styles.instructions}>
								<strong>Note:</strong> Answers are <em>not</em>{" "}
								case-sensitive, to make the password reset
								experience easier and less frustrating for you!
								😊
							</p>

							<form onSubmit={handleResetSubmit}>
								{securityQuestions.map((q) => (
									<div
										key={q.id}
										className={styles.formGroup}
									>
										<label>{q.question}</label>
										<input
											type="text"
											value={securityAnswers[q.id] || ""}
											onChange={(e) =>
												handleAnswerChange(
													q.id,
													e.target.value
												)
											}
											required
										/>
									</div>
								))}
								<div className={styles.formGroup}>
									<label>New Password:</label>
									<input
										type="password"
										value={newPassword}
										onChange={handleNewPasswordChange}
										onBlur={handleNewPasswordBlur}
										required
									/>
									<ul className={styles.passwordRequirements}>
										<li
											className={
												passwordRequirements.minLength
													? styles.valid
													: passwordTouched
													? styles.invalid
													: styles.defaultText
											}
										>
											At least 12 characters
										</li>
										<li
											className={
												passwordRequirements.hasSpecialChar
													? styles.valid
													: passwordTouched
													? styles.invalid
													: styles.defaultText
											}
										>
											At least one special character
											(one of the following: !@#$%^&*(),.?":{}|<></>) - don't be predictable and put it at the end! 😉
										</li>
									</ul>
								</div>
								{/* Update the confirm password input field in the form */}
								<div className={styles.formGroup}>
									<label>Confirm New Password:</label>
									<input
										type="password"
										value={confirmNewPassword}
										onChange={handleConfirmPasswordChange}
										onBlur={handleConfirmPasswordBlur}
										required
									/>
									{confirmPasswordTouched &&
										confirmPasswordError && (
											<p className={styles.errorMessage}>
												{confirmPasswordError}
											</p>
										)}
								</div>
								
								<button
									type="submit"
									className={styles.submitButton}
									disabled={
										// Disable the button if:
										// 1. There is a confirm password error (e.g., passwords do not match).
										// 2. The new password does not meet the minimum requirements:
										//    - At least 10 characters.
										//    - Contains at least one special character.
										// 3. Any required field is empty:
										//    - New password.
										//    - Confirm password.
										//    - Any security question answer.
										!!confirmPasswordError || 
										!passwordRequirements.minLength || 
										!passwordRequirements.hasSpecialChar || 
										!newPassword || 
										!confirmNewPassword || 
										securityQuestions.some((q) => !securityAnswers[q.id])
									}
								>
									Reset Password
								</button>
								{error && (
									<p className={styles.errorMessage}>
										{error}
									</p>
								)}
								{message && (
									// TODO: use success-message class name?
									<p className={styles.linkText}>{message}</p>
								)}
							</form>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default ResetForgottenPassword;
